import { css } from "lit";

export const styles = css`
  :host {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--sds-color-background-default-default);
    --wow-input-padding: 10px 12px;
    --wow-input-padding-inline: 12px;
  }

  .page-container {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    width: 100%;
    box-sizing: border-box;
  }

  /* Page-level two-column layout (desktop) */
  .page-layout {
    display: grid;
    grid-template-columns: 1fr 2fr;
    flex: 1;
    overflow: hidden;
  }

  .page-left-column {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
    padding: 24px 24px 0;
    border-right: 1px solid var(--sds-color-border-default-default);
    box-sizing: border-box;
  }

  .page-left-scroll {
    flex: 1;
    overflow: hidden auto;
    min-height: 0;
    padding-bottom: 24px;
  }

  .page-right-column {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    min-height: 0;
  }

  /* Page Header */
  .page-header {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-600);
    padding-bottom: var(--sds-size-space-1200);
  }

  .page-title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) var(--sds-typography-family-title);
    color: var(--sds-color-text-default-default);
    margin: 0;
    line-height: 20px;
  }

  .page-subtitle {
    font: var(--sds-typography-weight-regular) var(--sds-typography-body-size-medium) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-tertiary);
    margin: 0;
  }

  /* Event Info Tab (mobile only) */
  .event-info-tab {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    padding-top: 24px;
  }

  /* Promotion Tab (mobile only) */
  .promotion-tab {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    padding-top: 24px;
  }

  .promotion-tab > live-event-optional-toggles {
    --section-divider-margin-left: -24px;
    --section-divider-margin-right: -24px;
  }

  .promotion-divider {
    border: none;
    border-top: 1px solid var(--sds-color-border-default-default);
    margin: 0;
  }

  .section-divider {
    border: none;
    border-top: 1px solid var(--sds-color-border-default-default);
    margin: 0;
    padding: 0;
  }

  .form-panel > .section-divider {
    margin-left: -24px;
    margin-right: -24px;
  }

  .event-info-tab > .section-divider {
    margin-left: calc(-1 * var(--sds-size-space-800));
    margin-right: calc(-1 * var(--sds-size-space-800));
  }

  /* Form Panel */
  .form-panel {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    padding-top: 24px;
  }

  .form-panel > live-event-optional-toggles {
    --section-divider-margin-left: -24px;
    --section-divider-margin-right: -24px;
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .form-row {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .form-row > * {
    flex: 1;
  }

  /* Preview Panel (header label only) */
  .preview-panel {
    display: flex;
    flex-direction: column;
  }

  .preview-label {
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-title);
    font-size: var(--sds-typography-scale-05);
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    padding: var(--sds-size-space-1200) var(--sds-size-space-2400) 0;
  }

  .preview-subtitle {
    color: var(--sds-color-text-default-secondary);
    font-size: var(--sds-typography-body-size-medium);
    line-height: 1.4;
    padding: var(--sds-size-space-400) var(--sds-size-space-2400) var(--sds-size-space-1200);
    border-bottom: 1px solid var(--sds-color-border-default-default);
  }

  .upload-label {
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-title);
    font-size: var(--sds-typography-body-size-large);
    font-weight: var(--sds-typography-weight-medium);
    line-height: 1;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding: 0 var(--sds-size-space-2400);
  }

  .cover-uploader-container {
    display: flex;
    aspect-ratio: 16 / 9;
    border-radius: var(--sds-size-radius-400);
    overflow: hidden;
    padding: 0 var(--sds-size-space-2400);
  }

  .cover-uploader-divider {
    border-bottom: 1px solid var(--sds-color-border-default-default);
  }

  .preview-panel-content {
    padding: 0 var(--sds-size-space-2400);
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-1200);
  }

  .cover-uploader-container media-uploader {
    width: 100%;
    height: 100%;
  }

  /* Toggle Field */
  .toggle-field {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* Linked Links */
  .linked-links-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    background: var(--sds-color-background-default-secondary);
    border-radius: var(--sds-size-radius-400);
    max-height: 200px;
    overflow-y: auto;
  }

  .loading-links,
  .no-links {
    font: var(--sds-typography-weight-regular) var(--sds-typography-scale-01) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-tertiary);
    padding: 8px;
    text-align: center;
  }

  .links-checklist {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .link-checkbox-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px;
    border-radius: var(--sds-size-radius-400);
    cursor: pointer;
    transition: background-color 0.15s ease;
  }

  .link-checkbox-item:hover {
    background: var(--sds-color-background-default-tertiary);
  }

  .link-checkbox-item input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--sds-color-background-brand-default);
  }

  .link-label {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    font: var(--sds-typography-weight-regular) var(--sds-typography-scale-02) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-default);
  }

  .discount-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-00) var(--sds-typography-family-body);
    color: var(--sds-color-text-success-default);
    background: var(--sds-color-background-success-subtle);
    border-radius: var(--sds-size-radius-200);
    margin-left: auto;
  }

  /* Tickets Section */
  .tickets-row {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .tickets-row wow-input {
    flex: 1;
  }

  /* Footer */
  .page-footer {
    flex-shrink: 0;
    padding: 16px 24px;
    background: var(--sds-color-background-default-default);
    border-top: 1px solid var(--sds-color-border-default-default);
  }

  .footer-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  /* Stream Settings Tab */
  .stream-settings-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  /* Stream Preview Panel */
  .stream-preview-panel {
    display: flex;
    flex-direction: column;
  }

  .stream-preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 32px 24px;
    border-bottom: 1px solid #282828;
  }

  .stream-preview-label {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-05) var(--sds-typography-family-title);
    color: var(--sds-color-text-default-default);
  }

  .stream-preview-area {
    aspect-ratio: 16 / 9;
    background: rgb(0 0 0 / 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    position: relative;
    overflow: hidden;
  }

  .stream-preview-area stream-recorder,
  .stream-preview-area cdn-player {
    width: 100%;
    height: 100%;
  }

  .preview-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
  }

  /* OBS Configuration */
  .obs-config-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0;
  }

  .obs-steps {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 8px;
  }

  .obs-step {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .step-number {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--sds-color-background-default-default);
    color: var(--sds-color-text-default-default);
    border: 2px solid var(--sds-color-border-default-default);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font: var(--sds-typography-weight-semibold) var(--sds-typography-scale-01) var(--sds-typography-family-body);
  }

  .step-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  .step-title {
    font: var(--sds-typography-weight-semibold) var(--sds-typography-scale-02) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-default);
  }

  .step-description {
    font: var(--sds-typography-weight-regular) var(--sds-typography-scale-01) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-secondary);
    line-height: 150%;
  }

  .obs-credential-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .obs-credential-value {
    flex: 1;
    font: var(--sds-typography-weight-regular) var(--sds-typography-scale-01) var(--sds-typography-family-mono);
    color: var(--sds-color-text-default-default);
    background: var(--sds-color-background-default-tertiary);
    padding: 12px 16px;
    border-radius: var(--sds-size-radius-300);
    border: 1px solid var(--sds-color-border-default-default);
    overflow: hidden;
    word-break: break-all;
  }

  .obs-credential-value.masked {
    letter-spacing: 2px;
  }

  .obs-credential-value-wrapper {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
  }

  .obs-credential-value-wrapper .obs-credential-value {
    flex: 1;
    padding-right: 48px;
  }

  .obs-credential-value-wrapper .stream-key-toggle {
    position: absolute;
    right: 8px;
  }

  .problem-link {
    color: var(--sds-color-text-brand-default);
    text-decoration: underline;
    cursor: pointer;
  }

  .problem-link:hover {
    text-decoration: none;
  }

  /* Camera Permission */
  .permission-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border-radius: var(--sds-size-radius-400);
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-01) var(--sds-typography-family-body);
  }

  .permission-status.granted {
    background: var(--sds-color-background-success-subtle);
    color: var(--sds-color-text-success-default);
  }

  .permission-status.denied {
    background: var(--sds-color-background-danger-subtle);
    color: var(--sds-color-text-danger-default);
  }

  .permission-status.checking,
  .permission-status.prompt {
    background: var(--sds-color-background-default-secondary);
    color: var(--sds-color-text-default-secondary);
  }

  @media (max-width: 1100px) {
    .page-layout {
      grid-template-columns: 1fr 1fr;
    }

    .preview-label {
      padding: var(--sds-size-space-1200) var(--sds-size-space-1200) 0;
    }
    .preview-subtitle {
      padding: var(--sds-size-space-400) var(--sds-size-space-1200) var(--sds-size-space-1200);
    }
  }

  /* Responsive (mobile) */
  @media (max-width: 768px) {
    :host {
      overflow-y: auto;
    }

    .page-container {
      flex: none;
      overflow-y: visible;
      padding: var(--sds-size-space-800);
    }

    .page-header {
      padding-bottom: 0;
    }

    .mobile-title {
      font-size: 24px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
      padding: var(--sds-size-space-300) var(--sds-size-space-800) var(--sds-size-space-1200) var(--sds-size-space-800);
      margin-left: calc(-1 * var(--sds-size-space-800));
      margin-right: calc(-1 * var(--sds-size-space-800));
    }

    .promotion-tab > live-event-optional-toggles {
      --section-divider-margin-left: calc(-1 * var(--sds-size-space-800));
      --section-divider-margin-right: calc(-1 * var(--sds-size-space-800));
    }

    .page-footer {
      padding: 12px 16px;
    }

    .footer-actions {
      width: 100%;
    }

    .footer-actions wow-button {
      flex: 1;
    }
  }

  /* Custom input label color override */
  wow-input,
  wow-textarea,
  wow-select {
    --wow-input-label-color: var(--sds-color-text-default-tertiary);
  }

  wow-input,
  wow-select {
    --wow-input-placeholder-padding-top: 18px;
  }

  wow-select {
    --wow-input-label-white-space: normal;
  }

  /* Cover upload label */
  .upload-label {
    color: var(--sds-color-text-default-tertiary);
  }

  .input-icon-wrapper {
    position: relative;
    flex: 1;
  }

  .input-icon {
    position: absolute;
    right: 10px;
    bottom: 0;
    height: var(--wow-input-height, 56px);
    display: flex;
    align-items: center;
    pointer-events: none;
    color: var(--sds-color-text-default-secondary);
    line-height: 0;
  }
`;
